import React, { useEffect, useId } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal } from '@mui/material';

import { classes } from './styles';
import { SCOUT_BLUE } from '../../../ThemeContext/constants';
import { sharedClasses } from '../CustomUIElements/sharedClasses';

import type { SxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

const stopFocusBeforeDocumentTrap = (event: Event) => {
  event.stopPropagation();
};

const letPopupKeepFocusPastDocumentTrap = (selector: string, event: FocusEvent) => {
  const { target } = event;
  if (!(target instanceof Element)) return;
  const popup = target.closest(selector);
  if (!popup) return;

  popup.addEventListener('focusin', stopFocusBeforeDocumentTrap, { once: true });
};

const StyledModal = ({
  isOpen,
  handleClose,
  disableEnforceFocus = false,
  allowFocusSelector,
  styleOverrides,
  modalTitle,
  label,
  children,
  subHeaderStyling = true,
  closeIconId,
  modalId,
  showCloseIcon = true,
  titleSx
}: {
  isOpen: boolean;
  handleClose: () => void;
  styleOverrides?: SxProps<Theme>;
  modalTitle?: string;
  label: string;
  children?: React.ReactNode;
  subHeaderStyling?: boolean;
  closeIconId?: string;
  disableEnforceFocus?: boolean;
  allowFocusSelector?: string;
  modalId?: string;
  showCloseIcon?: boolean;
  titleSx?: SxProps<Theme>;
}) => {
  const titleId = useId();

  useEffect(() => {
    if (!isOpen || disableEnforceFocus || !allowFocusSelector) return undefined;

    const bypassDocumentTrap = (event: FocusEvent) => {
      letPopupKeepFocusPastDocumentTrap(allowFocusSelector, event);
    };

    document.addEventListener('focusin', bypassDocumentTrap, true);
    return () => document.removeEventListener('focusin', bypassDocumentTrap, true);
  }, [allowFocusSelector, disableEnforceFocus, isOpen]);

  return (
    <Modal disableEnforceFocus={disableEnforceFocus} onClose={handleClose} open={isOpen}>
      <Box
        aria-modal
        aria-label={modalTitle ? undefined : label}
        aria-labelledby={modalTitle ? titleId : undefined}
        id={modalId}
        role="dialog"
        sx={[classes.modal, styleOverrides ?? {}] as SxProps<Theme>}
      >
        {showCloseIcon ? (
          <IconButton
            aria-label="Close"
            id={closeIconId}
            onClick={handleClose}
            sx={
              [
                classes.closeIcon,
                { '&:focus-visible': { outline: `2px solid ${SCOUT_BLUE}`, outlineOffset: '2px' } }
              ] as SxProps<Theme>
            }
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
        {modalTitle ? (
          <Box
            aria-level={2}
            id={titleId}
            role="heading"
            sx={
              [
                subHeaderStyling ? classes.modalHeader : sharedClasses.sectionHeader,
                titleSx ?? {}
              ] as SxProps<Theme>
            }
          >
            {modalTitle}
          </Box>
        ) : null}
        {children}
      </Box>
    </Modal>
  );
};

export default StyledModal;
